import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Help, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import MetricsOverTime from './components/overTime';
import MetricsBreakdown from './components/breakdown';
import PeriodSelector from '../periodSelector';
import CompetencyList from 'components/general/competencyListNoRoute';

import './crmMetrics.css';
import RealtimeMetrics from '../realtimeMetrics';
import DataRow from '../dataRow';
import Rater from '../competencies/components/rater';
import posthog from 'posthog-js';

export const CRMMetrics = ({
    testId,
    profile,
    metrics,
    extraMetrics,
    updateRange,
    updateOffset,
    extraButtons,
    setCompetencies,
    realtimeMetrics,
    filteredMetric,
    setFilteredMetric,
    loading,
}) => {
    const [show, setShow] = React.useState(false);
    const [showBreakdown, setShowBreakdown] = React.useState(false);
    const [job, setJob] = React.useState(null);
    const [range, setRange] = React.useState('month');
    const [offset, setOffset] = React.useState(0);
    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        updateRange(range);
    }, [range]);

    React.useEffect(() => {
        updateOffset(offset);
    }, [offset]);

    if (!metrics || metrics.length === 0) return null;

    const buttons = () => (
        <PeriodSelector
            data-testid="periodselector-crm-metrics"
            range={range}
            setRange={setRange}
            offset={offset}
            setOffset={setOffset}
        />
    );

    return (
        <div data-testid={testId} className="metric121">
            <Box
                data-testid="user121-crmmetrics-box"
                title={SystemText('User121/CrmMetrics/Title')}
                button={buttons}
                preButton={extraButtons}
            >
                <div>
                    {metrics.map((m, i) => {
                        const title = `${m.type === 'M' ? SystemText('User121/CrmMetrics/StaffAveragePrefix') : ''}${
                            m.name
                        }`;

                        const help = (
                            <Help id={`metric-help-${m.job_Id}`}>
                                <h4>{title}</h4>
                                {m.description.replace(/(\r\n|\n|\r)/gm, '') && <div className="mb10">{m.description}</div>}
                                <CompetencyList competencies={m.competencies} />
                            </Help>
                        );
                        const special = (
                            <span className="ml10 mr10" style={{ position: 'relative', top: '-5px' }}>
                                <Rater
                                    hideOutOf
                                    benchmark={m.percentile}
                                    loading={loading}
                                    level={StringHelpers.formatMetric(m.format, m.value, user.currencySymbol)}
                                />
                            </span>
                        );

                        const showSecondBtn =
                            m.format !== 'P' &&
                            m.type !== 'M' &&
                            !m.calc_Days &&
                            !m.calc_Avg &&
                            m.extraFields &&
                            m.extraFields.length > 0;

                        const buttons = (
                            <>
                                <button
                                    data-testid="crmmetric-overtime-button"
                                    className="btn btn-xs btn-info"
                                    title={SystemText('User121/CrmMetrics/Buttons/Overtime/Hover')}
                                    onClick={() => {
                                        setShow(true);
                                        setJob(m);
                                        posthog.capture('show-metric-over-time', {
                                            integration: 'CRM',
                                            metric: m.name,
                                        });
                                    }}
                                >
                                    <i className="fa fa-line-chart"></i>
                                </button>

                                <button
                                    data-testid="crmmetric-breakdown-button"
                                    className="btn btn-xs btn-info"
                                    title={SystemText('User121/CrmMetrics/Buttons/Breakdown/Hover')}
                                    disabled={!m.value || !showSecondBtn}
                                    onClick={() => {
                                        setShowBreakdown(true);
                                        setJob(m);
                                    }}
                                >
                                    <i className="fa fa-pie-chart"></i>
                                </button>

                                <button
                                    data-testid="crmmetric-filter-button"
                                    className={`${
                                        filteredMetric === `M_${m.type}_${m.job_Id}` ? 'btn-selected' : ''
                                    } btn btn-xs btn-info`}
                                    title={SystemText('User121/CrmMetrics/Buttons/Filter/Hover')}
                                    onClick={() => {
                                        setCompetencies(m.competencies);
                                        setFilteredMetric(`M_${m.type}_${m.job_Id}`);
                                        posthog.capture('filter-capabilities-by-metric', {
                                            integration: 'CRM',
                                            metric: m.name,
                                        });
                                    }}
                                >
                                    <i className="fa fa-play"></i>
                                </button>
                            </>
                        );

                        return (
                            <DataRow
                                key={`metric-row-${m.id}-${i}`}
                                prefix={null}
                                title={title}
                                help={help}
                                special={special}
                                buttons={buttons}
                                testPrefix="crm-metric"
                            />
                        );
                    })}
                    <div className="col-xs-12 e4-data-row-container pt10">
                        <RealtimeMetrics
                            profile={profile}
                            realtimeMetrics={realtimeMetrics}
                            extraMetrics={extraMetrics}
                            integration="CRM"
                        />
                    </div>
                </div>

                {show && (
                    <MetricsOverTime
                        profile={profile}
                        job={show ? job : null}
                        range={range}
                        show={show}
                        close={() => {
                            setShow(false);
                            setJob(null);
                        }}
                    />
                )}
                <MetricsBreakdown
                    profile={profile}
                    job={showBreakdown ? job : null}
                    show={showBreakdown}
                    range={range}
                    offset={offset}
                    close={() => {
                        setShowBreakdown(false);
                        setJob(null);
                    }}
                />
            </Box>
        </div>
    );
};

export default CRMMetrics;
